/**
 * Privacy-Conscious Analytics for Naia Web Player
 * 
 * Tracks user interactions, performance metrics, and usage patterns
 * while respecting user privacy and GDPR compliance.
 */

import { logger } from '../utils/logger';

interface AnalyticsEvent {
  event: string;
  category: string;
  action: string;
  label?: string;
  value?: number;
  customDimensions?: Record<string, string | number>;
  timestamp: string;
  sessionId: string;
  userId?: string;
}

interface UserProperties {
  userId?: string;
  isAuthenticated: boolean;
  preferredTheme?: 'light' | 'dark';
  preferredLanguage?: string;
  deviceType?: 'desktop' | 'mobile' | 'tablet';
  region?: string;
}

interface PerformanceMetric {
  metric: string;
  value: number;
  category: 'page_load' | 'api_response' | 'user_interaction' | 'audio_playback';
  timestamp: string;
  additionalData?: Record<string, any>;
}

interface AnalyticsConfig {
  enabled: boolean;
  respectDNT: boolean; // Respect Do Not Track
  anonymizeIPs: boolean;
  cookieConsent: boolean;
  flushInterval: number;
  maxEventBuffer: number;
  environment: string;
  apiEndpoint?: string;
  googleAnalyticsId?: string;
}

class AnalyticsManager {
  private config: AnalyticsConfig;
  private eventBuffer: AnalyticsEvent[] = [];
  private userProperties: UserProperties = { isAuthenticated: false };
  private sessionId: string;
  private flushTimer?: number;
  private isInitialized = false;

  constructor(config: Partial<AnalyticsConfig> = {}) {
    this.config = {
      enabled: this.shouldEnableAnalytics(),
      respectDNT: true,
      anonymizeIPs: true,
      cookieConsent: false,
      flushInterval: 30000, // 30 seconds
      maxEventBuffer: 50,
      environment: this.getEnvironment(),
      ...config
    };

    this.sessionId = this.generateSessionId();
    this.setupAnalytics();
  }

  private shouldEnableAnalytics(): boolean {
    // Check if analytics should be enabled based on environment and user preferences
    const environment = this.getEnvironment();
    
    // Disable in development by default
    if (environment === 'development') {
      return false;
    }

    // Check Do Not Track header
    if (this.config?.respectDNT && navigator.doNotTrack === '1') {
      return false;
    }

    return true;
  }

  private getEnvironment(): string {
    if (typeof window !== 'undefined') {
      return (import.meta as any).env?.VITE_ENVIRONMENT || 'development';
    }
    return 'development';
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupAnalytics(): void {
    if (!this.config.enabled) {
      logger.info('Analytics disabled', { component: 'Analytics' });
      return;
    }

    // Setup flush timer
    if (typeof window !== 'undefined') {
      this.flushTimer = window.setInterval(() => {
        this.flush();
      }, this.config.flushInterval);

      // Flush on page unload
      window.addEventListener('beforeunload', () => {
        this.flush();
      });

      // Track page visibility changes
      document.addEventListener('visibilitychange', () => {
        this.trackEvent({
          event: 'page_visibility_change',
          category: 'engagement',
          action: document.visibilityState,
          customDimensions: {
            timestamp: new Date().toISOString()
          }
        });
      });
    }

    // Setup Google Analytics if configured
    this.setupGoogleAnalytics();

    this.isInitialized = true;
    logger.info('Analytics initialized', { 
      component: 'Analytics',
      metadata: { sessionId: this.sessionId, environment: this.config.environment }
    });
  }

  private setupGoogleAnalytics(): void {
    if (!this.config.googleAnalyticsId || typeof window === 'undefined') {
      return;
    }

    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${this.config.googleAnalyticsId}`;
    document.head.appendChild(script);

    // Initialize gtag
    (window as any).dataLayer = (window as any).dataLayer || [];
    function gtag(...args: any[]) {
      (window as any).dataLayer.push(args);
    }

    gtag('js', new Date());
    gtag('config', this.config.googleAnalyticsId, {
      anonymize_ip: this.config.anonymizeIPs,
      respect_dnt: this.config.respectDNT,
      send_page_view: false // We'll send manually
    });

    (window as any).gtag = gtag;
  }

  // Public API
  initialize(config: Partial<AnalyticsConfig> = {}): void {
    this.config = { ...this.config, ...config };
    if (!this.isInitialized) {
      this.setupAnalytics();
    }
  }

  setUserProperties(properties: Partial<UserProperties>): void {
    this.userProperties = { ...this.userProperties, ...properties };
    
    if (this.config.enabled) {
      logger.debug('User properties updated', {
        component: 'Analytics',
        metadata: { properties: this.userProperties }
      });
    }
  }

  trackEvent(event: Omit<AnalyticsEvent, 'timestamp' | 'sessionId'>): void {
    if (!this.config.enabled) {
      return;
    }

    const analyticsEvent: AnalyticsEvent = {
      ...event,
      timestamp: new Date().toISOString(),
      sessionId: this.sessionId,
      userId: this.userProperties.userId
    };

    this.eventBuffer.push(analyticsEvent);

    // Send to Google Analytics if configured
    if ((window as any).gtag && this.config.googleAnalyticsId) {
      (window as any).gtag('event', event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
        custom_map: event.customDimensions
      });
    }

    // Flush if buffer is full
    if (this.eventBuffer.length >= this.config.maxEventBuffer) {
      this.flush();
    }

    logger.debug('Analytics event tracked', {
      component: 'Analytics',
      metadata: { event: analyticsEvent }
    });
  }

  // Convenience methods for common events
  trackPageView(page: string, title?: string): void {
    this.trackEvent({
      event: 'page_view',
      category: 'navigation',
      action: 'page_view',
      label: page,
      customDimensions: {
        page_title: title || document.title,
        page_location: window.location.href,
        page_path: window.location.pathname
      }
    });
  }

  trackUserAction(action: string, element: string, details?: Record<string, any>): void {
    this.trackEvent({
      event: 'user_action',
      category: 'interaction',
      action,
      label: element,
      customDimensions: details
    });
  }

  trackSongPlay(songId: string, artistId: string, source: string): void {
    this.trackEvent({
      event: 'song_play',
      category: 'music',
      action: 'play',
      label: songId,
      customDimensions: {
        artist_id: artistId,
        source,
        user_authenticated: this.userProperties.isAuthenticated ? 'yes' : 'no'
      }
    });
  }

  trackSongComplete(songId: string, duration: number, completionRate: number): void {
    this.trackEvent({
      event: 'song_complete',
      category: 'music',
      action: 'complete',
      label: songId,
      value: Math.round(completionRate * 100),
      customDimensions: {
        duration_seconds: duration,
        completion_rate: completionRate
      }
    });
  }

  trackSearch(query: string, resultCount: number, source: string): void {
    this.trackEvent({
      event: 'search',
      category: 'search',
      action: 'query',
      label: this.anonymizeSearchQuery(query),
      value: resultCount,
      customDimensions: {
        source,
        has_results: resultCount > 0 ? 'yes' : 'no'
      }
    });
  }

  trackError(error: string, component: string, severity: 'low' | 'medium' | 'high'): void {
    this.trackEvent({
      event: 'error',
      category: 'error',
      action: severity,
      label: component,
      customDimensions: {
        error_message: this.anonymizeError(error),
        timestamp: new Date().toISOString()
      }
    });
  }

  trackPerformance(metric: PerformanceMetric): void {
    if (!this.config.enabled) {
      return;
    }

    this.trackEvent({
      event: 'performance',
      category: 'performance',
      action: metric.category,
      label: metric.metric,
      value: Math.round(metric.value),
      customDimensions: {
        ...metric.additionalData,
        performance_metric: metric.metric
      }
    });
  }

  // Performance tracking helpers
  trackPageLoadTime(): void {
    if (typeof window !== 'undefined' && window.performance) {
      const loadTime = window.performance.timing.loadEventEnd - window.performance.timing.navigationStart;
      
      this.trackPerformance({
        metric: 'page_load_time',
        value: loadTime,
        category: 'page_load',
        timestamp: new Date().toISOString(),
        additionalData: {
          dom_content_loaded: window.performance.timing.domContentLoadedEventEnd - window.performance.timing.navigationStart,
          first_paint: window.performance.timing.loadEventStart - window.performance.timing.navigationStart
        }
      });
    }
  }

  trackAPICall(endpoint: string, method: string, responseTime: number, status: number): void {
    this.trackPerformance({
      metric: 'api_response_time',
      value: responseTime,
      category: 'api_response',
      timestamp: new Date().toISOString(),
      additionalData: {
        endpoint: this.anonymizeEndpoint(endpoint),
        method,
        status_code: status,
        success: status < 400 ? 'yes' : 'no'
      }
    });
  }

  // Privacy helpers
  private anonymizeSearchQuery(query: string): string {
    // Remove potentially sensitive information from search queries
    return query.length > 50 ? `${query.substring(0, 47)}...` : query;
  }

  private anonymizeError(error: string): string {
    // Remove potentially sensitive information from error messages
    return error.replace(/\b\d{4}-\d{2}-\d{2}\b/g, '[DATE]')
                .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g, '[EMAIL]')
                .replace(/\b\d{3}-\d{2}-\d{4}\b/g, '[SSN]');
  }

  private anonymizeEndpoint(endpoint: string): string {
    // Remove IDs and sensitive parameters from endpoints
    return endpoint.replace(/\/\d+/g, '/[ID]')
                  .replace(/[?&]id=\d+/g, '&id=[ID]')
                  .replace(/[?&]token=[^&]+/g, '&token=[TOKEN]');
  }

  // Data management
  private async flush(): Promise<void> {
    if (this.eventBuffer.length === 0) {
      return;
    }

    const eventsToSend = [...this.eventBuffer];
    this.eventBuffer = [];

    try {
      if (this.config.apiEndpoint) {
        await fetch(this.config.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            events: eventsToSend,
            userProperties: this.userProperties,
            sessionId: this.sessionId,
            environment: this.config.environment,
            timestamp: new Date().toISOString()
          })
        });
      }

      logger.debug('Analytics events flushed', {
        component: 'Analytics',
        metadata: { eventCount: eventsToSend.length }
      });
    } catch (error) {
      // If sending fails, add events back to buffer
      this.eventBuffer.unshift(...eventsToSend);
      
      logger.warn('Failed to send analytics events', {
        component: 'Analytics',
        metadata: { error: error instanceof Error ? error.message : 'Unknown error' }
      });
    }
  }

  enableAnalytics(): void {
    this.config.enabled = true;
    logger.info('Analytics enabled', { component: 'Analytics' });
  }

  disableAnalytics(): void {
    this.config.enabled = false;
    this.eventBuffer = [];
    logger.info('Analytics disabled', { component: 'Analytics' });
  }

  getCookieConsent(): boolean {
    return this.config.cookieConsent;
  }

  setCookieConsent(consent: boolean): void {
    this.config.cookieConsent = consent;
    if (!consent) {
      this.disableAnalytics();
    } else {
      this.enableAnalytics();
    }
  }

  // Cleanup
  destroy(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
    }
    this.flush();
  }
}

// Create singleton instance
const analytics = new AnalyticsManager();

// Export convenience functions
export const trackPageView = (page: string, title?: string) => analytics.trackPageView(page, title);
export const trackUserAction = (action: string, element: string, details?: Record<string, any>) => analytics.trackUserAction(action, element, details);
export const trackSongPlay = (songId: string, artistId: string, source: string) => analytics.trackSongPlay(songId, artistId, source);
export const trackSongComplete = (songId: string, duration: number, completionRate: number) => analytics.trackSongComplete(songId, duration, completionRate);
export const trackSearch = (query: string, resultCount: number, source: string) => analytics.trackSearch(query, resultCount, source);
export const trackError = (error: string, component: string, severity: 'low' | 'medium' | 'high') => analytics.trackError(error, component, severity);
export const trackPerformance = (metric: PerformanceMetric) => analytics.trackPerformance(metric);
export const trackPageLoadTime = () => analytics.trackPageLoadTime();
export const trackAPICall = (endpoint: string, method: string, responseTime: number, status: number) => analytics.trackAPICall(endpoint, method, responseTime, status);

// Export configuration functions
export const initializeAnalytics = (config: Partial<AnalyticsConfig>) => analytics.initialize(config);
export const setUserProperties = (properties: Partial<UserProperties>) => analytics.setUserProperties(properties);
export const enableAnalytics = () => analytics.enableAnalytics();
export const disableAnalytics = () => analytics.disableAnalytics();
export const setCookieConsent = (consent: boolean) => analytics.setCookieConsent(consent);

// Export main instance and types
export { analytics };
export type { AnalyticsEvent, UserProperties, PerformanceMetric, AnalyticsConfig }; 